import { useStorage, type RemovableRef } from "@vueuse/core";
import { defineStore } from "pinia";
import type { RouteLocationNormalized } from "vue-router";

type ClientStore = {
  client: RemovableRef<string>;
  theme: string;
  phoneNumber: string;
  whatsappNumber: string;
};

export enum ClientName {
  Galicia = "galicia",
  RioUruguaySeguros = "rus",
  Rapihogar = "rapihogar",
  Meridional = "meridional",
  Soelsac = "soelsac",
}

export const useClientStore = defineStore("clientStore", {
  state: (): ClientStore => ({
    client: useStorage("client", ""),
    theme: "",
    phoneNumber: "",
    whatsappNumber: "",
  }),

  getters: {
    formatPhoneNumber(state) {
      const phone = state.phoneNumber.replace(/ /g, "");
      return `tel:+54 ${phone}`;
    },
    formatWhatsappNumber(state) {
      const whatsapp = state.whatsappNumber.replace(/ /g, "");
      return `https://wa.me/54${whatsapp}`;
    },
    headerTheme(state) {
      if (state.client === ClientName.RioUruguaySeguros) {
        return `${state.theme} md:shadow-none`;
      }
      return `${state.theme} md:shadow-md`;
    },
  },
  actions: {
    setClientName() {
      if (
        import.meta.env.VITE_CLIENT_NAME === ClientName.Rapihogar ||
        import.meta.env.VITE_CLIENT_NAME === ""
      ) {
        this.client = "rapihogar";
        this.theme = "theme-rapi-hogar";
        this.phoneNumber = "0810 555 2255";
        this.whatsappNumber = "11 4857 7777";
      }
      if (import.meta.env.VITE_CLIENT_NAME === ClientName.RioUruguaySeguros) {
        this.client = "rus";
        this.theme = "theme-rus";
        this.phoneNumber = "351 5760609";
        this.whatsappNumber = "11 5996 3708";
      }
      if (import.meta.env.VITE_CLIENT_NAME === ClientName.Galicia) {
        this.client = "galicia";
        this.theme = "theme-galicia";
        this.phoneNumber = "0800 555 2272";
        this.whatsappNumber = "11 3254 8281";
      }
      if (import.meta.env.VITE_CLIENT_NAME === ClientName.Meridional) {
        this.client = "meridional";
        this.theme = "theme-meridional";
        this.phoneNumber = "+5493515760671";
        this.whatsappNumber = "";
      }
      if (import.meta.env.VITE_CLIENT_NAME === ClientName.Soelsac) {
        this.client = "soelsac";
        this.theme = "theme-soelsac";
        this.phoneNumber = "";
        this.whatsappNumber = "";
      }
    },
    getOrganizationName(clientName: string) {
      const organizationNames: Record<string, string> = {
        rapihogar: "Vaitty",
        rus: "Río Uruguay Seguros",
        galicia: "Galicia Seguros",
        meridional: "Meridional Seguros",
        soelsac: "Soelsac",
      };

      return organizationNames[clientName] || "Vaitty";
    },
    getMetaTitleByOrg(
      routeName: RouteLocationNormalized["name"],
      clientName: string
    ) {
      const titlesByOrg: Record<string, Record<string, string>> = {
        rus: {
          Requests: "Mis asistencias",
          RequestsCreate: "Nueva asistencia",
          RequestView: "Detalle asistencia",
        },
        galicia: {
          Requests: "Mis asistencias",
          RequestsCreate: "Nueva asistencia",
          RequestView: "Detalle asistencia",
        },
        meridional: {
          Requests: "Mis solicitudes",
          RequestsCreate: "Nueva solicitud",
          RequestView: "Detalle de solicitud",
        },
        soelsac: {
          Requests: "Mis solicitudes",
          RequestsCreate: "Nueva solicitud",
          RequestView: "Detalle solicitud",
        },
        rapihogar: {
          Requests: "Mis solicitudes",
          RequestsCreate: "Nueva solicitud",
          RequestView: "Detalle solicitud",
        },
      };

      const orgTitles = titlesByOrg[clientName] || {};

      return orgTitles[String(routeName) || ""] || "";
    },
  },
});
